import { useCallback, useEffect, useMemo } from 'react';
import { FormikProps } from 'formik';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import {
  StyledContainer,
  StyledSettingsContainer,
  InputCounter,
  StyledFormGroup,
  StyledSubTitle,
  StyledText,
  StyledLink,
} from '@selfserve/react-ui';
import { billingCycles, billingCycleType } from '@selfserve/react-utils';

import { PackageDetail } from '../data';
import { FormValues } from '../feature/build-your-plan';
import { AddPlan } from '@selfserve/react-feature-account-management';

import { planControllers } from '../constants';

const StyledPlanName = styled(StyledText)`
  line-height: 26px;
`;

const StyledPlansIncludedContainer = styled.div`
  line-height: 20px;
`;

const StyledRoomSelectContainer = styled.div(({ theme }: { theme?: any }) => ({
  marginTop: theme.spacing(2),
  '> p': {
    marginBottom: theme.spacing(1),
  },
}));

const StyledNoPlanTextContainer = styled.div(({ theme }: { theme?: any }) => ({
  display: 'flex',
  alignItems: 'center',
  '> p': {
    marginRight: theme.spacing(1),
  },
}));

interface Props extends PackageDetail {
  formik: FormikProps<FormValues>;
  disabled: boolean;
}

export const YourPlan = ({
  formik: {
    setFieldValue,
    values: { billingCycle, plan },
  },
  disabled,
  plans,
  leading_plan,
  description,
  what_does_it_include,
}: Props) => {
  const { t } = useTranslation();
  const currentPlan = useMemo(
    () => plans?.find(({ name }) => leading_plan?.name === name),
    [plans]
  );
  const hasPlan = useMemo(
    () =>
      !disabled &&
      leading_plan &&
      (!leading_plan.hasOwnProperty('visible') || leading_plan.visible),
    [leading_plan, disabled]
  );
  const yearlyPricing = useMemo(
    () =>
      currentPlan?.pricing_info?.find(
        (p) => p.billing_cycle === billingCycleType.YEAR
      ),
    [currentPlan]
  );
  const monthlyPricing = useMemo(
    () =>
      currentPlan?.pricing_info?.find(
        (p) => p.billing_cycle === billingCycleType.MONTH
      ),
    [currentPlan]
  );
  const pricingInfo = useMemo(() => {
    switch (billingCycle) {
      default:
      case billingCycles.ANNUALLY:
        return yearlyPricing;
      case billingCycles.MONTHLY:
        return monthlyPricing;
    }
  }, [yearlyPricing, monthlyPricing, billingCycle]);

  const onLinkClickHandle = useCallback(() => {
    window.open(what_does_it_include, '_blank');
  }, []);

  const handleChange = useCallback(
    (key: string, value: string | number) => {
      const { name } = currentPlan || {};
      setFieldValue(key, { name, quantity: +value });
    },
    [plan, currentPlan]
  );

  useEffect(() => {
    if (currentPlan) {
      const { product } = currentPlan || {};
      const { currency } = pricingInfo || {};
      setFieldValue('product', product);
      setFieldValue('currency', currency);
    }
  }, [currentPlan, pricingInfo]);

  useEffect(() => {
    if (currentPlan) {
      const { name } = currentPlan || {};
      if (!yearlyPricing) {
        (setFieldValue('billingCycle', billingCycles.MONTHLY) as any).then(() =>
          setFieldValue('plan', { ...plan, name })
        );
      } else {
        setFieldValue('plan', { ...plan, name });
      }
    }
  }, [currentPlan, yearlyPricing]);

  return (
    <StyledContainer fitHeight>
      <StyledSubTitle className="sub-header" as="span">
        {t('common.your-plan')}
      </StyledSubTitle>
      {hasPlan ? (
        <StyledSettingsContainer>
          <StyledPlanName className="plan-name" bold>
            {description}
          </StyledPlanName>
          <StyledContainer className="plan-info" row flexWrap>
            {currentPlan?.description && (
              <StyledText className="plan-info-room" withDot>
                {currentPlan.description}
              </StyledText>
            )}
            <StyledText className="plan-info-price" withDot>
              ${pricingInfo?.price || 0}/{t(`common.${billingCycle}`)}
            </StyledText>
            <StyledText>{t(`common.billed-${billingCycle}`)}</StyledText>
          </StyledContainer>
          {leading_plan?.controller !== planControllers.NONE && (
            <>
              <StyledPlansIncludedContainer className="plan-includes">
                <StyledLink to="" onClick={onLinkClickHandle}>
                  {t('common.whats-included')}
                </StyledLink>
              </StyledPlansIncludedContainer>
              {((controller) => {
                switch (controller) {
                  case planControllers.COUNTER:
                    return (
                      <StyledRoomSelectContainer>
                        <StyledText className="plan-room-question">
                          {leading_plan?.headline || t('common.how-many-rooms')}
                        </StyledText>
                        <StyledFormGroup withoutMargin rowWithLabel>
                          <InputCounter
                            id={leading_plan?.name as string}
                            name="plan"
                            value={plan.quantity as number}
                            onChange={handleChange}
                          />
                          <label htmlFor="room-selection">
                            {t('common.rooms')}
                          </label>
                        </StyledFormGroup>
                      </StyledRoomSelectContainer>
                    );
                  default:
                    return null;
                }
              })(leading_plan?.controller)}
            </>
          )}
        </StyledSettingsContainer>
      ) : (
        <StyledNoPlanTextContainer>
          <StyledText>{t('common.no-plan')}</StyledText>
          <AddPlan isLink />
        </StyledNoPlanTextContainer>
      )}
    </StyledContainer>
  );
};
